'use client'

import { useTranslation } from 'react-i18next'
import { Flex, Grid, Text, VStack } from '@chakra-ui/react'
import { DonationTypeBadge, ResultBadge } from '@/entities/donor/ui'
import type { IDonationHistory } from '@/entities/donor/model'
import { AppCard } from '@/shared'

interface Props {
  donations: IDonationHistory[]
}

export function DonorDonationHistoryCard({ donations }: Props) {
  const { t } = useTranslation()

  const columns = [
    t('donor_detail.history.date'),
    t('donor_detail.history.number'),
    t('donor_detail.history.type'),
    t('donor_detail.history.volume'),
    // t('donor_detail.history.institution'),
    t('donor_detail.history.result'),
  ]

  return (
    <AppCard.Root h="full">
      <AppCard.Body p={5}>
        <Flex justify="space-between" align="center" mb={4}>
          <Text fontWeight="semibold" fontSize="sm">
            {t('donor_detail.history.title')}
          </Text>
          <Text
            fontSize="sm"
            color="primary.fg"
            cursor="pointer"
            fontWeight="medium"
            _hover={{ textDecoration: 'underline' }}
          >
            {t('donor_detail.history.all')}
          </Text>
        </Flex>

        <Grid
          templateColumns="1fr 1.3fr 1fr 1fr 1fr"
          gap={2}
          pb={2}
          borderBottomWidth="1px"
          px={'10px'}
          borderColor="border"
        >
          {columns.map((col) => (
            <Text
              key={col}
              fontSize="xs"
              color="fg.muted"
              fontWeight="semibold"
            >
              {col}
            </Text>
          ))}
        </Grid>

        <VStack
          gap={0}
          align="stretch"
          bg="bg.inset"
          borderRadius="md"
          px="10px"
          h="100%"
        >
          {!donations.length && (
            <Text fontSize="sm" color="fg.muted" py={3}>
              {t('donor_detail.history.empty')}
            </Text>
          )}
          {donations.map((d) => (
            <Grid
              key={d.donation_uuid}
              templateColumns="1fr 1.3fr 1fr 1fr 1fr"
              gap={2}
              py={3}
              borderColor="border.muted"
              _last={{ borderBottom: 'none' }}
              alignItems="center"
            >
              <Text fontSize="sm" color="fg.muted">
                {d.date}
              </Text>
              <Text fontSize="sm" color="fg.muted" truncate>
                {d.donation_number || '—'}
              </Text>
              <DonationTypeBadge
                label={d.donation_type}
                type={d.donation_type}
              />
              <Text fontSize="sm" color="fg.muted" truncate>
                {d.volume_ml} {t('donor_detail.history.ml')}
              </Text>
              <ResultBadge result={d?.status} />
            </Grid>
          ))}
        </VStack>
      </AppCard.Body>
    </AppCard.Root>
  )
}
